import {
  AMENITY_SPACES_CATEGORY,
  CUSTOM_SUBCATEGORY,
  MEETING_SPACES_CATEGORY,
  PRIVATE_OFFICES_SUBCATEGORY,
  SUPPORT_SPACES_CATEGORY,
  WORKSTATIONS_SUBCATEGORY,
} from 'spw-constants';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectSpacesMap } from 'spw-redux/modules/floorplan/selectors';
import { sumUSF } from 'spw-shared/utils';
import { ISpace } from 'spw-redux/modules/floorplan/types';
import { selectCurrentUserIsViewOnly } from 'spw-redux/modules/projects/selectors';

export const useSubtotalUSF = () => {
  const spacesMap: Map<string, ISpace[]> = useSelector(selectSpacesMap);

  return useMemo(() => {
    const individualWorkSpaces = (spacesMap.get(WORKSTATIONS_SUBCATEGORY) || [])
      .concat(spacesMap.get(PRIVATE_OFFICES_SUBCATEGORY) || [])
      .concat(spacesMap.get(CUSTOM_SUBCATEGORY) || []);
    const meetingSpaces = spacesMap.get(MEETING_SPACES_CATEGORY) || [];
    const amenitySpaces = spacesMap.get(AMENITY_SPACES_CATEGORY) || [];
    const supportSpaces = spacesMap.get(SUPPORT_SPACES_CATEGORY) || [];

    return sumUSF([
      ...individualWorkSpaces,
      ...meetingSpaces,
      ...amenitySpaces,
      ...supportSpaces,
    ]);
  }, [spacesMap]);
};

export const useEnterShortcut = ({
  onEnterKeyPressed,
  enabled,
}: {
  onEnterKeyPressed: () => void;
  enabled: boolean;
}) => {
  const onEnterKeyPressedRef = useRef(onEnterKeyPressed);
  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);

  onEnterKeyPressedRef.current = onEnterKeyPressed;

  const handleEnterKeyPressed = useCallback(
    (event: KeyboardEvent) => {
      if (
        event.key === 'Enter' &&
        onEnterKeyPressedRef.current &&
        enabled &&
        !event.shiftKey &&
        !currentUserIsViewOnly
      ) {
        onEnterKeyPressedRef.current();
        event.preventDefault();
      }
    },
    [enabled, currentUserIsViewOnly]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEnterKeyPressed);
    return () => {
      document.removeEventListener('keydown', handleEnterKeyPressed);
    };
  }, [handleEnterKeyPressed]);
};
